import React, { useState } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import DashboardHome from './DashboardHome';
import GroupManagement from './GroupManagement';
import UserManagement from './UserManagement';
import ProductManagement from './ProductManagement';
import ThresholdManagement from './ThresholdManagement';
import CommissionRatioManagement from './CommissionRatioManagement';
import MyGroup from './MyGroup';
import SalesManagement from './SalesManagement';
import MyCommissions from './MyCommissions';
import CommissionManagement from './CommissionManagement';
import AdminAnalytics from './AdminAnalytics';
import UserAnalytics from './UserAnalytics';
import Reports from './Reports';

const Dashboard = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');

  const renderContent = () => {
    switch (currentView) {
      case 'group-management':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Group Management</h1>
                <p className="text-gray-600 dark:text-dark-muted mt-1">Create and manage organizational groups</p>
              </div>
              <GroupManagement />
            </div>
          </main>
        );
      case 'user-management':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">User Management</h1>
                <p className="text-gray-600 dark:text-dark-muted mt-1">Create and manage system users</p>
              </div>
              <UserManagement />
            </div>
          </main>
        );
      case 'product-management':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Products/Campaigns Management</h1>
                <p className="text-gray-600 dark:text-dark-muted mt-1">Create and manage products/campaigns with commission rates and thresholds</p>
              </div>
              <ProductManagement />
            </div>
          </main>
        );
      case 'threshold-management':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Threshold Management</h1>
                <p className="text-gray-600 dark:text-dark-muted mt-1">Create and manage campaign thresholds for override eligibility</p>
              </div>
              <ThresholdManagement />
            </div>
          </main>
        );
      case 'commission-ratio-management':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Commission Ratio Management</h1>
                <p className="text-gray-600 dark:text-dark-muted mt-1">Manage commission distribution ratios for Group Leaders, Team Leaders, and Agents</p>
              </div>
              <CommissionRatioManagement />
            </div>
          </main>
        );
      case 'my-group':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">My Group</h1>
                <p className="text-gray-600 dark:text-dark-muted mt-1">View your team members and group hierarchy</p>
              </div>
              <MyGroup user={user} />
            </div>
          </main>
        );
      case 'sales-management':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              <SalesManagement />
            </div>
          </main>
        );
      case 'my-commissions':
        // Show CommissionManagement for Admin/Superadmin, MyCommissions for others
        const isAdminCommission = user?.role === 'Superadmin' || user?.role === 'Admin';
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg p-4 lg:p-6 transition-colors duration-200">
            <div className="w-full max-w-none">
              {isAdminCommission ? <CommissionManagement /> : <MyCommissions />}
            </div>
          </main>
        );
      case 'analytics':
        // Show AdminAnalytics for Admin/Superadmin, UserAnalytics for others
        const isAdmin = user?.role === 'Superadmin' || user?.role === 'Admin';
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg transition-colors duration-200">
            <div className="w-full max-w-none">
              {isAdmin ? <AdminAnalytics /> : <UserAnalytics />}
            </div>
          </main>
        );
      case 'reports':
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg transition-colors duration-200">
            <div className="w-full max-w-none">
              <Reports />
            </div>
          </main>
        );
      default:
        return (
          <main className="flex-1 bg-gray-50 dark:bg-dark-bg transition-colors duration-200">
            <DashboardHome user={user} onNavigate={setCurrentView} />
          </main>
        );
    }
  };

  return (
    <div className="flex bg-gray-50 dark:bg-dark-bg min-h-screen transition-colors duration-200">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        onNavigate={setCurrentView}
        currentView={currentView}
        user={user}
        onLogout={onLogout}
      />
      
      {/* Main Content - Add left margin for fixed sidebar on desktop */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Header */}
        <Header onMenuClick={() => setSidebarOpen(true)} user={user} />
        
        {/* Dynamic Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default Dashboard;
